/**
 * Customer Management React Query Hooks
 * Provides data fetching hooks for customers
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { customerApi, type CustomerApiFilters, type CreateCustomerData, type UpdateCustomerData } from '../api/customers';
import { toast } from 'sonner';

/** Extract a human-readable message from an axios/unknown error. */
function errorMessage(error: unknown, fallback: string): string {
  if (isAxiosError<{ message?: string }>(error)) {
    return error.response?.data?.message || error.message || fallback;
  }
  if (error instanceof Error) return error.message || fallback;
  return fallback;
}

// Query keys for cache management
export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  list: (filters: CustomerApiFilters, page: number, limit: number) =>
    [...customerKeys.lists(), { filters, page, limit }] as const,
  details: () => [...customerKeys.all, 'detail'] as const,
  detail: (id: string) => [...customerKeys.details(), id] as const,
  billing: (id: string) => [...customerKeys.detail(id), 'billing'] as const,
};

/**
 * Hook to fetch paginated customers with filters
 */
export function useCustomers(filters: CustomerApiFilters = {}, page = 1, limit = 20) {
  return useQuery({
    queryKey: customerKeys.list(filters, page, limit),
    queryFn: async () => {
      return await customerApi.getCustomers(filters, page, limit);
    },
  });
}

/**
 * Hook to fetch single customer by ID
 */
export function useCustomer(id: string) {
  return useQuery({
    queryKey: customerKeys.detail(id),
    queryFn: async () => {
      return await customerApi.getCustomerById(id);
    },
    enabled: !!id,
  });
}

/**
 * Hook to fetch a customer's billing/purchase history
 */
export function useCustomerBillingHistory(id: string) {
  return useQuery({
    queryKey: customerKeys.billing(id),
    queryFn: async () => {
      return await customerApi.getBillingHistory(id);
    },
    enabled: !!id,
  });
}

/**
 * Hook to change a customer's subscription plan (upgrade/downgrade, no payment).
 */
export function useChangeCustomerPlan(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (planId: string) => {
      return await customerApi.changeSubscriptionPlan(customerId, planId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.detail(customerId) });
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      toast.success('Subscription plan changed');
    },
    onError: (error: unknown) => {
      toast.error(errorMessage(error, 'Failed to change subscription plan'));
    },
  });
}

/**
 * Hook to cancel a customer's active subscription.
 */
export function useCancelCustomerSubscription(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await customerApi.cancelSubscription(customerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.detail(customerId) });
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      toast.success('Subscription cancelled');
    },
    onError: (error: unknown) => {
      toast.error(errorMessage(error, 'Failed to cancel subscription'));
    },
  });
}

/**
 * Hook to create a new customer
 */
export function useCreateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCustomerData) => {
      return await customerApi.createCustomer(data);
    },
    onSuccess: () => {
      // Invalidate all customer lists to refetch data
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      toast.success('Customer created successfully');
    },
    onError: (error: unknown) => {
      toast.error(errorMessage(error, 'Failed to create customer'));
    },
  });
}

/**
 * Hook to update an existing customer
 */
export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateCustomerData }) => {
      return await customerApi.updateCustomer(id, data);
    },
    onSuccess: (_, variables) => {
      // Invalidate customer lists and specific detail
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customerKeys.detail(variables.id) });
      toast.success('Customer updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(errorMessage(error, 'Failed to update customer'));
    },
  });
}

/**
 * Hook to delete a customer
 */
export function useDeleteCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await customerApi.deleteCustomer(id);
    },
    onSuccess: (_, id) => {
      // Invalidate customer lists and specific detail
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customerKeys.detail(id) });
      toast.success('Customer deleted successfully');
    },
    onError: (error: unknown) => {
      toast.error(errorMessage(error, 'Failed to delete customer'));
    },
  });
}

